import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import makeStyles from "@material-ui/core/styles/makeStyles";
import Stepper from "@material-ui/core/Stepper";
import Step from "@material-ui/core/Step";
import {Paper} from "@material-ui/core";
import Divider from "@material-ui/core/Divider";
import StepButton from "@material-ui/core/StepButton";
import {del, insert, set} from 'object-path-immutable'
import {useHistory, useLocation} from 'react-router-dom';
import {useSnackbar} from "notistack";
import request from "../../Services/API/Requests";
import getSafe from "../JsonScreen/Utils/getSafeValues";
import findByString from "../../Utils/Form/FormDynamic/Utils/getValueByString";
import {successMessage} from "../JsonScreen/Utils";
import GeneralInformation from "./GeneralInformation";
import SchemaProperties from "./SchemaProperties";
import Api from "./Api";
import OtherSettings from "./OtherSettings";
import Confirmation from "../../Utils/Confirmation/Confirmation";
import SchemaDesignerActions from "./SchemaDesignerActions";

const useStyles = makeStyles(() => ({
    container: {
        width: 1000
    },
    content: {
        height: '80vh',
        maxHeight: 600,
        display: 'flex',
        overflowY: 'auto',
        flexDirection: 'column'
    },
    contentContainer: {
        flexGrow: 1,
        overflow: 'hidden'
    }
}));
const ALL_STEPS = [
    {label: 'General Information', hidden: false, stepId: 0},
    {label: 'Fields', hidden: false, stepId: 1},
    {label: 'API', hidden: false, stepId: 2},
    {label: 'Other Settings', hidden: false, stepId: 3}
];

/**
 *
 * @param hasCollection Boolean
 * @param hiddenSteps Array of Int, Where every item mean number of step (1-based)
 * @returns {*} Boolean
 */
function getSteps(hasCollection, hiddenSteps = []) {
    let copySteps = JSON.parse(JSON.stringify(ALL_STEPS)); //full shallow copy of array, to avoid mutations
    if (hiddenSteps.length) {
        for (let hiddenStep of hiddenSteps) {
            copySteps[hiddenStep - 1].hidden = true
        }
        let filtered = copySteps.filter(i => !i.hidden);
        return {
            availableSteps: filtered,
            activeStep: filtered.length ? filtered[0].stepId : 0
        };
    }
    return hasCollection ?
        {availableSteps: ALL_STEPS, activeStep: 0} :
        {availableSteps: ALL_STEPS.slice(0, 3), activeStep: 0}
}

const SchemaDesigner = (props) => {
    const {match: {params: {id}}, isPopup, closePopup, returnValue, inParam, doActions} = props;
    const {enqueueSnackbar} = useSnackbar();
    const history = useHistory();
    const location = useLocation();
    const classes = useStyles();
    const [loading, setLoading] = useState(false);
    const [confirmation, setConfirmation] = useState(false);
    const [data, setData] = useState({properties: []});
    const [inheritedData, setInheritedData] = useState({});
    const [steps, setSteps] = useState(ALL_STEPS);
    const [activeStep, setActiveStep] = useState(0);

    useEffect(() => {
        if (inParam.defaultValues) {


            async function setDefaultValues() {
                let currentDefaultValues = JSON.parse(JSON.stringify(inParam.defaultValues));
                if (inParam.defaultValues.inherit) {
                    let inheritedSchema = await getInheritedSchema(inParam.defaultValues.inherit)

                    currentDefaultValues.properties = [
                        ...inheritedSchema.properties,
                        ...(inParam.defaultValues.properties || [])
                    ]
                }
                setData(prev => {
                    return {
                        ...prev, ...currentDefaultValues
                    }
                })
            }

            setDefaultValues();
        }

        if (inParam.hiddenSteps) {
            const {availableSteps, activeStep} = getSteps(false, inParam.hiddenSteps);
            setSteps(availableSteps);
            setActiveStep(activeStep)
        }

        if (inParam.id) {
            getSchema(inParam.id)
        }
    }, []);

    useEffect(() => {
        getSchema(id)
    }, [id]);

    useEffect(() => {
        const {
            availableSteps,
            activeStep
        } = getSteps(Boolean(data.collection || inheritedData.collection), inParam.hiddenSteps);
        setSteps(availableSteps);
        setActiveStep(activeStep)
    }, [data.collection, inheritedData.id]);

    async function getSchema(id) {
        try {
            if (id) {
                const response = await request({
                    url: "schema/readWithApi",
                    data: {id}
                });
                const {availableSteps, activeStep} = getSteps(Boolean(response.collection), inParam.hiddenSteps);
                setData(response);
                setSteps(availableSteps);
                setActiveStep(activeStep);
                return
            }
            const {availableSteps, activeStep} = getSteps(false, inParam.hiddenSteps);
            setSteps(availableSteps);
            setActiveStep(activeStep)

        } catch (e) {
            let message = getSafe(() => e.data.status.message[0].text, 'Error');
            const {availableSteps, activeStep} = getSteps(false, inParam.hiddenSteps);
            setSteps(availableSteps);
            setActiveStep(activeStep);
            enqueueSnackbar(message, {variant: 'error'});
        }
    }

    async function getInheritedSchema(id) {
        try {
            const inheritedSchema = await request({
                url: "/schema/readWithApi",
                data: {id}
            })
            return {
                ...inheritedSchema,
                properties: inheritedSchema.properties.map(v => {
                    return {
                        ...v,
                        tmp: true,
                        inherit: true
                    }
                })
            }
        } catch (e) {
            console.log('get inherited schema error', e);
            return {properties: []}
        }
    }

    async function onFinish() {
        try {
            setLoading(true);
            const filtered = data.properties.filter(i => !i.tmp);
            const response = await request({
                url: `/schema/update`,
                data: {
                    ...data,
                    properties: filtered
                }
            });
            setLoading(false);
            if (!isPopup) {
                enqueueSnackbar(successMessage(
                        response.id, history, location.pathname, location.state.prevState),
                    {variant: `success`}
                );
                history.goBack();
                return
            }

            doActions({type: "returnValue", param: [{value: [response.id]}]})
            closePopup();

        } catch (e) {
            console.log(e)
            setLoading(false);
            let message = getSafe(() => e.data.status.message[0].text, 'Error');
            enqueueSnackbar(message, {variant: 'error'});
        }
    }

    async function onDelete() {
        try {
            await request({
                url: '/schema/delete',
                method: 'POST',
                data: {id}
            });
            enqueueSnackbar('Success', {variant: 'success'});
            history.goBack();
        } catch (e) {
            setLoading(false);
            let message = getSafe(() => e.data.status.message[0].text, 'Error');
            enqueueSnackbar(message, {variant: 'error'});
        }
    }

    function changeStep(step) {
        setActiveStep(step);

        //check for collection & existent endPoints in data, if null get baseEndpoints
        if (data.id && data.collection && (!data.endPoints || !data.endPoints.length)) {
            async function getEndpoints() {
                const response = await request({
                    url: 'schema/baseEndpoints',
                    data: {
                        id: data.id,
                        name: data.name,
                        collection: data.collection
                    }
                });
                setData(prevData => {
                    return {
                        ...prevData, endPoints: (response.endPoints || []).map(i => {
                            return {...i, active: true}
                        })
                    }
                });
            }

            getEndpoints();
            return
        }

        // if (!data.collection) {
        //     setData(prev => ({
        //         ...prev,
        //         endPoints: []
        //     }))
        // }
    }

    function onChangeFieldsProperties(value, path, fieldName, isOverridable) {
        let newValues = set(data, fieldName ? path + '.' + fieldName : path, value);
        if (isOverridable) {
            newValues = set(newValues, path + '.' + 'overridden', true)
        }

        setData(newValues)
    }

    async function onChangeFields(value, path) {

        let newValues = set(data, path, value);

        if (path === 'inherit' && value) {
            const inheritedSchema = await getInheritedSchema(value)

            //clear prev inherited fields
            if (newValues.properties && newValues.properties.length) {
                newValues.properties = newValues.properties.filter(v => !v.tmp)
            }

            //create temporary properties
            newValues.properties = [
                ...inheritedSchema.properties,
                ...newValues.properties,
            ]

            setInheritedData(inheritedSchema)
        } else if (path === 'inherit' && !value) {
            newValues.properties = newValues.properties.filter(v => !v.inherit)
        }

        setData(newValues)
    }

    function createEndpointEvent(value, path, index) {
        let newValues = insert(data, path, value, index)
        setData(newValues)
    }

    function removeEndpointEvent(path) {
        let newValues = del(data, path);
        setData(newValues)
    }

    /* API HANDLERS */
    function onChangeCheckboxAll(checked, path) {
        let value = findByString(data, path);
        const newChecked = value.map(item => {
            return {...item, active: checked}
        });
        const newValues = set(data, path, newChecked);
        setData(newValues)
    }

    function createNewItem(path, index) {
        const newPath = path ? `properties.${path}` : 'properties';
        const newValues = insert(data, newPath, {name: "newItem", label: "New Item"}, index);
        setData(newValues)
    }

    function insertItems(items) {
        setData(prev => {
            return {
                ...prev,
                properties: [...prev.properties, ...items]
            }
        })
    }

    function deleteItem(path) {
        let newValues = del(data, `properties.${path}`);
        setData(newValues)
    }

    function getStepContent(step) {
        console.log(data)
        switch (step) {
            case 0:
                return <GeneralInformation
                    setValue={onChangeFields}
                    values={data}
                />;
            case 1:
                return <SchemaProperties
                    data={data.properties}
                    setValue={onChangeFieldsProperties}
                    createNewItem={createNewItem}
                    deleteItem={deleteItem}
                    insertItems={insertItems}
                />;
            case 2:
                return <Api
                    onChangeItem={onChangeFields}
                    onChangeAll={onChangeCheckboxAll}
                    createEndpointEvent={createEndpointEvent}
                    removeEndpointEvent={removeEndpointEvent}
                    endpoints={data.endPoints}
                    inheritedEndpoints={inheritedData.endPoints}
                    inheritedValidations={inheritedData.recordValidations}
                    validations={data.recordValidations}
                />;
            case 3:
                return <OtherSettings
                    schema={data.properties}
                    listFields={data.listFields}
                    labelFields={data.labelFields}
                    dropDownFields={data.dropDownFields}
                    editScreen={data.editScreen}
                    setValue={onChangeFields}
                />;
            default:
                return <></>
        }
    }

    return (
        <div className={classes.container} style={{margin: isPopup ? 0 : '10px auto'}}>
            <Paper className={classes.content} style={{marginTop: isPopup ? 0 : 10}}>
                <Stepper activeStep={activeStep} nonLinear>
                    {steps.map((step) => (
                            <Step key={step.label}>
                                <StepButton onClick={() => changeStep(step.stepId)}>
                                    {step.label}
                                </StepButton>
                            </Step>
                        )
                    )}
                </Stepper>
                <Divider/>
                <div className={classes.contentContainer}>
                    {getStepContent(activeStep)}
                </div>
                <Divider/>
                <SchemaDesignerActions
                    changeStep={changeStep}
                    onFinish={onFinish}
                    currentStep={activeStep}
                    steps={steps}
                    lastStep={steps.length}
                    isUpdate={Boolean(id)}
                    openConfirmation={() => setConfirmation(true)}
                    cancel={() => isPopup ? closePopup() : history.goBack()}
                />
                <Confirmation
                    open={confirmation}
                    close={() => setConfirmation(false)}
                    submit={onDelete}
                />
            </Paper>
        </div>
    )
};

export default SchemaDesigner

SchemaDesigner.propTypes = {
    computedMatch: PropTypes.object,
    returnValue: PropTypes.func,
    closePopup: PropTypes.func,
    isPopup: PropTypes.bool,
    inParam: PropTypes.shape({
        defaultValues: PropTypes.object,
        hiddenSteps: PropTypes.array
    })
};

SchemaDesigner.defaultProps = {
    inParam: {}
};


function addInherit(data) {
    return data.map(item => {
        let newItem = {...item, inherit: true};
        if (item.type === 'entityEmbedded' || item.type === 'object') {
            if (newItem.schema) {
                newItem.schema.properties = addInherit(item.schema.properties)
            }
        }
        return newItem;
    });
}
